import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { AlertTriangle, BookOpen, Check, CheckCircle2, CircleDot, Clock3, FileText, RefreshCw } from 'lucide-react';
import {
  activateFacilityContractVersion,
  apiErrorMessage,
  getFacilityContractVersion,
  getFacilityContractVersions,
  resolveFacilityVersionAsKnownAt,
  stageManualFacilityAuthority,
  stageRuleClassificationCorrection,
} from '../api/abfApi';
import type { VersionAsKnownAt } from '../types/amendmentReview';
import { DOCUMENT_PROCESSING_PATH, amendmentReviewPath, onboardingVersionPath } from '../routes';
import StructuredAmendmentForm from '../amendments/StructuredAmendmentForm';
import { Chip, Table } from '../amendments/AmendmentGroupPane';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import { ActionLocalFeedback } from '../components/ActionFeedback';
import { StatusChip, type StatusTone } from '../components/StatusChip';
import { businessLabel, businessState, displayContractValue, readJsonValue, termGroup } from '../presentation/contractPresentation';
import type { FacilityContractVersion, FacilityTermRecord } from '../types/contractWorkspace';
import { getCurrentOperatorUsername } from '../../../utils/currentOperator';
import { Button, Card, CardHead, INK, fieldBase } from '../onboarding/review/reviewStyles';
import { Layout, Note, Readable, SmallButton, TaskButton, TaskRail, Work } from '../onboarding/review/contractReviewStyles';
import { WorkspaceGuide, WorkspaceTools } from '../components/WorkspaceExperience';
import CollectionsSchedulesPanel from './CollectionsSchedulesPanel';
import { ContractSourceDrawer, SourceLink, useProvenance, type SourceRequest } from './ContractSourcePanel';
import AccrualBasisPanel from './AccrualBasisPanel';
import TermOutPanel from './TermOutPanel';
import ManualAuthorityEditor, {
  emptyManualAuthority,
  manualAuthorityPayload,
  validateManualAuthority,
  type ManualAuthorityDraft,
} from '../onboarding/ManualAuthorityEditor';
import { DateField } from '../../../components/shared/DateField';
import { fmtDate, fmtDateTime, fmtMoney, fmtNumber, labelFor, plural } from '../presentation/format';

/**
 * The Contract tab, on the review standard (Slice 6, Decision 1.5 revised 2026-09-21).
 *
 * Until this rebuild the tab still carried its Slice 3 markup: a "changed-term comparison"
 * card, a "materialized schedule" card of counts, and the source clause parked in a side
 * card the reader had to look across to. Every other tab of the facility workspace had by
 * then been rebuilt on the same journey strip, metric strip, task rail, work pane and sticky
 * decision bar. This is that rebuild for the contract.
 *
 * The rail lists the versions, newest first, and closes with the bi-temporal "as known at"
 * control (FR-6.15 is seen here, not just stored). The work pane shows the selected
 * version's terms and schedules grouped the way the amendment review groups them — Terms,
 * Eligibility and concentration, Covenants, Waterfall and consequences, Calendar and
 * accounts, Lender classes — each row with its own source clause beside it. The decision
 * bar carries the one next action: enter or upload an amendment, open the review of the
 * one that is staged, or activate a verified onboarding version under the four-eyes rule.
 *
 * Nothing governed changed: activation, staging and the rule-classification correction
 * call the same endpoints they did, with the same refusals shown before the click.
 */

const Shell = styled.div`
  color: ${INK.body}; font-size: 14px; line-height: 1.55; margin-top: 14px;
  h2, h3, p { margin: 0; } h2 { font-size: 17px; } h3 { font-size: 15px; }
  button, input, select, textarea { font-family: inherit; }
`;

const Journey = styled.ol`
  display: grid; grid-template-columns: repeat(3, 1fr); list-style: none;
  padding: 0; margin: 0 0 18px; border: 1px solid ${INK.line}; border-radius: 10px; overflow: hidden;
  li { display: flex; gap: 12px; align-items: center; padding: 13px 16px; background: #0c1626; }
  li + li { border-left: 1px solid ${INK.line}; }
  li[data-current='true'] { background: #12273c; box-shadow: inset 0 -2px ${INK.accent}; }
  .step-icon { display: grid; place-items: center; width: 28px; height: 28px; border: 1px solid ${INK.lineStrong}; border-radius: 50%; color: #b5c9dd; flex-shrink: 0; }
  .done { color: #7cddbe; border-color: #376e60; }
  strong { display: block; font-size: 14px; }
  small { display: block; color: ${INK.muted}; font-size: 12px; margin-top: 2px; }
  @media (max-width: 700px) { grid-template-columns: 1fr; li + li { border-left: 0; border-top: 1px solid ${INK.line}; } }
`;

const Strip = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr)); gap: 1px;
  margin-bottom: 18px; border: 1px solid ${INK.line}; border-radius: 10px;
  background: ${INK.line}; overflow: hidden;
  > div { padding: 11px 14px; background: ${INK.panel}; }
  > div[data-tone='warn'] { background: #1d1a12; }
  span { display: block; color: ${INK.faint}; font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; }
  strong { display: block; margin: 5px 0 2px; color: ${INK.strong}; font-size: 19px; font-weight: 800; overflow-wrap: anywhere; }
  strong[data-tone='warn'] { color: #fcd34d; } strong[data-tone='good'] { color: #7cddbe; }
  small { color: ${INK.muted}; font-size: 12px; line-height: 1.4; }
`;

const Bar = styled.footer`
  position: sticky; bottom: 0; z-index: 20; display: flex; flex-wrap: wrap; gap: 12px;
  align-items: center; justify-content: space-between; margin-top: 16px;
  padding: 12px 16px; border: 1px solid #43617f; border-radius: 10px;
  background: rgba(20, 36, 56, .96); backdrop-filter: blur(6px); box-shadow: 0 8px 32px #0006;
  strong, .state { display: block; }
  .state { color: ${INK.soft}; font-size: 13px; margin-top: 3px; max-width: 80ch; }
  .actions { display: flex; flex-wrap: wrap; gap: 8px; }
  a { text-decoration: none; }
  @media (max-width: 700px) { flex-direction: column; align-items: stretch; }
`;

/** In-page section links for the long version detail; the cards carry matching ids. */
const SectionNav = styled.nav`
  position: sticky; top: 0; z-index: 5; display: flex; flex-wrap: wrap; gap: 4px; align-items: center;
  margin: 0 0 12px; padding: 6px 8px; border: 1px solid ${INK.line}; border-radius: 9px;
  background: rgba(8, 16, 30, .92); backdrop-filter: blur(6px);
  > span { color: ${INK.faint}; font-size: 11px; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; margin-right: 6px; }
  a { padding: 4px 8px; border-radius: 6px; color: ${INK.accentSoft}; font-size: 12px; font-weight: 650; text-decoration: none; }
  a:hover, a:focus-visible { background: ${INK.well}; color: ${INK.strong}; outline: none; }
`;

const GroupHead = styled.h3`
  margin: 14px 0 2px !important; color: #7dd3fc; font-size: 12px !important; font-weight: 750; letter-spacing: .05em; text-transform: uppercase;
`;

const Before = styled.span`color: ${INK.muted}; text-decoration: line-through; text-decoration-color: ${INK.faint};`;
const Arrow = styled.span`color: ${INK.faint}; margin: 0 6px;`;

const KnownAt = styled.div`
  display: grid; gap: 8px;
  .pair { display: grid; grid-template-columns: 1fr auto; gap: 6px; }
  input { ${fieldBase} min-height: 36px; }
  input[type='time'] { width: 6.6rem; }
  .verdict { padding: 10px 12px; border-left: 2px solid ${INK.accent}; background: ${INK.well}; border-radius: 6px; font-size: 12px; color: ${INK.soft}; }
  .verdict strong { display: block; color: ${INK.strong}; font-size: 13px; margin-bottom: 3px; }
  .verdict ul { margin: 6px 0 0; padding-left: 14px; color: ${INK.muted}; }
  .problem { color: #fcd34d; font-size: 12px; }
`;

const Empty = styled.p`
  color: ${INK.muted}; font-size: 13px; padding: 8px 0 4px;
`;

const Failure = styled.p`
  margin: 0 0 .7rem; padding: .55rem .65rem;
  border: 1px solid rgba(248,113,113,.4); border-radius: 8px;
  background: rgba(127,29,29,.18); color: #fecaca; font-size: 13px; line-height: 1.5;
`;

// ---------------------------------------------------------------------------

const roleAliases: Record<string, string> = {
  PM: 'PORTFOLIO_MANAGER', RISK: 'RISK_MANAGER', COMPLIANCE: 'COMPLIANCE_OFFICER', OPERATIONS: 'OPERATIONS_ANALYST',
};
const checkerRoles = new Set(['RISK_MANAGER', 'COMPLIANCE_OFFICER', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);
const makerRoles = new Set(['PORTFOLIO_MANAGER', 'TRADER', 'OPERATIONS_ANALYST', 'ADMIN', 'PLATFORM_ADMIN']);

const date = (value: string | null | undefined) => fmtDate(value, 'not recorded');

const dateTime = (value: string | null | undefined) => fmtDateTime(value, 'not recorded');

/** F-S6-029: a knowledge (system-time) instant in the reader's local time, never the UTC calendar day. */
const knownTime = (value: string | null | undefined) => value
  ? new Intl.DateTimeFormat('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value))
  : 'not recorded';
const knownClock = (value: string) => new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(new Date(value));
const sameLocalDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

const number = (value: number | null | undefined, digits = 2) => fmtNumber(value, digits);

const money = (value: number | null | undefined, currency: string | null | undefined) => fmtMoney(value, currency || 'USD', 0, '—');

const plain = (value: string | null | undefined) => value ? value.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()) : '—';

const stateTone = (state: string): StatusTone => state === 'ACTIVE' ? 'done'
  : ['VERIFIED', 'SCHEDULED'].includes(state) ? 'progress'
    : ['REJECTED', 'WITHDRAWN'].includes(state) ? 'critical'
      : state === 'SUPERSEDED' ? 'neutral' : 'warning';

const parseChangeSummary = (version: FacilityContractVersion): Record<string, unknown> => {
  const value = readJsonValue(version.change_summary_json);
  return value && typeof value === 'object' ? value as Record<string, unknown> : {};
};

const confidence = (value: number | null) => value == null ? null
  : `${Math.round(Number(value) <= 1 ? Number(value) * 100 : Number(value))}% confidence`;

/** A term as the source panel shows it (F-MK-084). */
const termRequest = (term: FacilityTermRecord, after: unknown, detail: string | null, material: boolean): SourceRequest => ({
  uid: `term:${term.id}`, table: 'terms', row: term as unknown as Record<string, unknown>, kind: 'TERM', section: 'terms',
  label: businessLabel(term.term_key), group: termGroup(term.term_key), value: displayContractValue(after, term.value_presence),
  detail, page: term.source_page, sectionRef: term.source_section, clause: term.source_clause,
  confidence: term.extraction_confidence, required: term.required_for_activation, material,
});

interface Props {
  facilityId?: number;
  versionId?: number;
  userRole?: string | null;
  onFeedback: (feedback: ActionFeedbackState | null) => void;
}

const ContractWorkspace: React.FC<Props> = ({ facilityId, versionId, userRole, onFeedback }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [termSearch, setTermSearch] = useState('');
  const [changedOnly, setChangedOnly] = useState(false);
  const [versions, setVersions] = useState<FacilityContractVersion[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(versionId || null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [localFeedback, setLocalFeedback] = useState<ActionFeedbackState | null>(null);
  const [manualAuthority, setManualAuthority] = useState<ManualAuthorityDraft>(emptyManualAuthority);
  const [amendmentOpen, setAmendmentOpen] = useState(false);
  const [knownAtDate, setKnownAtDate] = useState('');
  const [knownAtTime, setKnownAtTime] = useState('23:59');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [asKnown, setAsKnown] = useState<VersionAsKnownAt | null>(null);
  const [asKnownError, setAsKnownError] = useState<string | null>(null);
  // F-MK-084: the row whose source is open in the side panel.
  const [openSource, setOpenSource] = useState<SourceRequest | null>(null);
  const closeSource = useCallback(() => setOpenSource(null), []);
  const provenance = useProvenance(versions);
  const workRef = useRef<HTMLElement>(null);

  const load = useCallback(async (notify = false) => {
    setLoading(true);
    try {
      const result = versionId
        ? [await getFacilityContractVersion(versionId)]
        : await getFacilityContractVersions(Number(facilityId));
      const ordered = [...result].sort((a, b) => b.version_no - a.version_no);
      setVersions(ordered);
      const requested = Number(searchParams.get('version'));
      const next = ordered.find(item => item.id === (versionId || requested))
        || ordered.find(item => item.state === 'ACTIVE')
        || ordered[0] || null;
      setSelectedId(current => ordered.some(item => item.id === current) && !requested ? current : next?.id || null);
      if (notify) {
        const feedback = { tone: 'success' as const, message: 'Contract authority and version history refreshed.' };
        setLocalFeedback(feedback); onFeedback(feedback);
      }
    } catch (error) {
      const feedback = { tone: 'error' as const, message: apiErrorMessage(error, 'Unable to load contract versions.') };
      setLocalFeedback(feedback); onFeedback(feedback);
    } finally { setLoading(false); }
  }, [facilityId, onFeedback, searchParams, versionId]);

  useEffect(() => { void load(); }, [load]);

  const selected = versions.find(item => item.id === selectedId) || null;
  const active = versions.find(item => item.state === 'ACTIVE') || null;
  const openAmendment = versions.find(item => ['VERIFIED', 'SCHEDULED'].includes(item.state) && item.predecessor_version_id != null) || null;
  const predecessor = selected?.predecessor_version_id
    ? versions.find(item => item.id === selected.predecessor_version_id) || null
    : null;
  const predecessorTerms = useMemo(() => new Map((predecessor?.terms || []).map(term => [term.term_key, term])), [predecessor]);

  // Terms grouped as the amendment review groups them; on an amendment version the changed
  // terms carry their prior value and the rest are shown as inherited.
  const termRows = useMemo(() => {
    if (!selected) return [] as Array<{ term: FacilityTermRecord; prior: FacilityTermRecord | undefined; before: unknown; after: unknown; changed: boolean; material: boolean }>;
    const impactByKey = new Map(selected.amendmentImpact.map(item => [item.term_key, item]));
    const summary = parseChangeSummary(selected);
    const changedKeys = new Set(Array.isArray(summary.changedTerms) ? summary.changedTerms.map(String) : []);
    return selected.terms
      .map(term => {
        const impact = impactByKey.get(term.term_key);
        const prior = predecessorTerms.get(term.term_key);
        const changed = Boolean(impact) || changedKeys.has(term.term_key);
        return {
          term, prior,
          before: impact ? impact.before_value_json : prior?.verified_value_json,
          after: impact ? impact.after_value_json : term.verified_value_json,
          changed, material: Boolean(impact?.material),
        };
      })
      .sort((left, right) => Number(right.changed) - Number(left.changed)
        || termGroup(left.term.term_key).localeCompare(termGroup(right.term.term_key))
        || businessLabel(left.term.term_key).localeCompare(businessLabel(right.term.term_key)));
  }, [predecessorTerms, selected]);

  const changedCount = termRows.filter(row => row.changed).length;
  const verifiedCount = selected?.terms.filter(term => term.state === 'VERIFIED' || term.verified_by).length || 0;

  const normalizedRole = roleAliases[String(userRole || '').toUpperCase()] || String(userRole || '').toUpperCase();
  const canCheck = checkerRoles.has(normalizedRole);
  const canMake = makerRoles.has(normalizedRole);
  const me = (getCurrentOperatorUsername() || '').toLowerCase();
  const sameVerifier = Boolean(selected?.verified_by && me && selected.verified_by.toLowerCase() === me);
  const openActivationConditions = selected?.conditionsPrecedent.filter(
    item => item.required_for === 'ACTIVATION' && !['SATISFIED', 'WAIVED'].includes(item.status),
  ) || [];
  const collectionsUnresolved = (selected?.contractConfiguration?.unresolved || [])
    .filter(item => item.startsWith('accounts') || item === 'paymentCalendar')
    .map(item => {
      const key = item.split('.')[1];
      const account = selected?.contractConfiguration?.accounts?.find(a => a.accountKey === key);
      return item === 'paymentCalendar' ? 'the payment calendar'
        : item.endsWith('.maskedAccountNumber') ? `the ${(account?.accountName || 'account').toLowerCase()} number`
          : 'an account the document states incompletely';
    });
  const completedAccountByMe = Boolean((selected?.contractConfiguration?.accounts || []).some(account =>
    account.completion?.completedBy && me && account.completion.completedBy.toLowerCase() === me));
  const activationRefusal = !selected ? 'Select a contract version.'
    : selected.state !== 'VERIFIED' ? `This version is ${businessState(selected.state).toLowerCase()}.`
      : selected.completeness_status !== 'COMPLETE' ? 'Required contract terms, eligibility definitions or covenants remain unresolved.'
        : collectionsUnresolved.length > 0 ? `Complete ${collectionsUnresolved.join(' and ')} under Calendar and accounts first.`
          : completedAccountByMe ? 'Four-eyes: you completed a contract account, so a different checker activates this version.'
            : openActivationConditions.length > 0 ? `${plural(openActivationConditions.length, 'activation condition')} precedent ${openActivationConditions.length === 1 ? 'remains' : 'remain'} open.`
              : sameVerifier ? 'Four-eyes: the user who verified this version cannot activate it.'
                : !canCheck ? `Independent activation needs a risk, compliance, operations or administrator role; yours is ${businessLabel(userRole)}.`
                  : null;

  const selectVersion = (id: number) => {
    setSelectedId(id); setFailure(null); setAmendmentOpen(false); setOpenSource(null);
    if (!versionId) {
      const next = new URLSearchParams(searchParams);
      next.set('version', String(id));
      setSearchParams(next, { replace: true });
    }
    if (window.matchMedia('(max-width: 1000px)').matches) workRef.current?.scrollIntoView({ block: 'start' });
  };

  const act = async (action: () => Promise<unknown>, pending: string, success: (result: any) => string, fallback: string) => {
    setBusy(true); setFailure(null);
    const info = { tone: 'info' as const, message: pending };
    setLocalFeedback(info); onFeedback(info);
    try {
      const result = await action();
      const feedback = { tone: 'success' as const, message: success(result) };
      setLocalFeedback(feedback); onFeedback(feedback);
      await load(false);
      return result;
    } catch (error) {
      const message = apiErrorMessage(error, fallback);
      setFailure(message);
      const feedback = { tone: 'error' as const, message };
      setLocalFeedback(feedback); onFeedback(feedback);
      return null;
    } finally { setBusy(false); }
  };

  const activate = () => selected && !activationRefusal && void act(
    () => activateFacilityContractVersion(selected.id, { notes: 'Activated from the facility workspace contract tab.' }),
    `Activating contract version ${selected.version_no}…`,
    result => `Contract version ${(result as FacilityContractVersion).version_no} is active. No exposure, cash or borrowing base was posted by this action.`,
    'Contract activation failed.',
  );

  const stageManual = () => {
    if (!facilityId || busy) return;
    const validation = validateManualAuthority(manualAuthority);
    if (validation) { setFailure(validation); return; }
    void act(
      () => stageManualFacilityAuthority(facilityId, manualAuthorityPayload(manualAuthority) as Record<string, unknown>),
      'Staging the contract authority…',
      result => `Contract version ${(result as FacilityContractVersion).version_no} is verified and waits for a different user to activate it.`,
      'The contract authority could not be staged.',
    );
  };

  const prepareRuleCorrection = async () => {
    if (!selected || busy) return;
    const draft = await act(
      () => stageRuleClassificationCorrection(selected.id),
      'Preparing the corrected version…',
      result => `Version ${(result as FacilityContractVersion).version_no} is prepared with the rule corrections. An independent checker activates it.`,
      'The rule correction could not be prepared.',
    ) as FacilityContractVersion | null;
    if (draft) selectVersion(draft.id);
  };

  const resolveAsKnown = async () => {
    if (!facilityId || !knownAtDate || !effectiveAt) return;
    try {
      setAsKnownError(null);
      setAsKnown(await resolveFacilityVersionAsKnownAt(facilityId, effectiveAt, `${knownAtDate}T${knownAtTime || '23:59'}:00`));
    } catch (error) {
      setAsKnown(null);
      setAsKnownError(apiErrorMessage(error, 'No version applied on that date as the record stood then.'));
    }
  };

  // ------------------------------------------------------------------ empty and loading states

  if (loading && versions.length === 0) return <Shell><Note>Loading the contract authority and version history…</Note></Shell>;

  if (!selected) return <Shell>
    <Journey aria-label="How contract authority is established">
      <li data-current="true"><span className="step-icon">1</span><div><strong>The agreement is entered</strong><small>From the executed document, or by hand when there is no machine-readable copy</small></div></li>
      <li><span className="step-icon">2</span><div><strong>A different user activates it</strong><small>Four-eyes: whoever entered it cannot activate it</small></div></li>
      <li><span className="step-icon">3</span><div><strong>Collateral runs against it</strong><small>Eligibility, covenants and the waterfall all read the active version</small></div></li>
    </Journey>
    <Readable>
      <Card>
        <CardHead><div><h2>Establish contract authority</h2><p>This facility has no contract version. Enter the executed agreement's eligibility and covenant definitions; a different user must activate them before collateral can be recalculated. A PDF of the agreement can instead be uploaded under Middle Office → Document Processing.</p></div></CardHead>
        <ManualAuthorityEditor value={manualAuthority} onChange={setManualAuthority} />
        {failure && <Failure role="alert">{failure}</Failure>}
        <ActionLocalFeedback feedback={localFeedback} />
      </Card>
    </Readable>
    <Bar>
      <div><strong>Stage for independent activation</strong><span className="state">{!canMake ? 'A portfolio manager, trader, operations user or administrator stages the authority.' : !facilityId ? 'A persisted facility is required.' : 'The version is staged as verified; a different checker activates it.'}</span></div>
      <div className="actions"><Button $variant="primary" type="button" disabled={busy || !canMake || !facilityId} onClick={stageManual}><CheckCircle2 size={14} /> Stage version 1</Button></div>
    </Bar>
  </Shell>;

  // ------------------------------------------------------------------ derived presentation

  const journeyStep: 1 | 2 | 3 = openAmendment ? (openAmendment.state === 'SCHEDULED' ? 3 : 2)
    : selected.state === 'VERIFIED' ? 3 : 1;
  const isAmendment = selected.predecessor_version_id != null;
  const currency = String(readJsonValue(selected.terms.find(term => term.term_key === 'currency')?.verified_value_json) || 'USD');
  const visibleTerms = termRows.filter(row => (!changedOnly || row.changed) && `${businessLabel(row.term.term_key)} ${displayContractValue(row.after)} ${row.term.source_clause || ''}`.toLowerCase().includes(termSearch.trim().toLowerCase()));
  const groupedTerms = visibleTerms.reduce<Array<{ name: string; rows: typeof termRows }>>((groups, row) => {
    const name = termGroup(row.term.term_key);
    const group = groups.find(item => item.name === name);
    if (group) group.rows.push(row); else groups.push({ name, rows: [row] });
    return groups;
  }, []);
  const changedGroups = groupedTerms.map(group => ({ ...group, rows: group.rows.filter(row => row.changed) })).filter(group => group.rows.length);
  const inheritedRows = visibleTerms.filter(row => !row.changed);
  const scheduleCount = selected.eligibilityRules.length + selected.covenants.length + selected.waterfallNodes.length + selected.lenderClasses.length
    + ((selected.contractAccounts || []).length || (selected.contractConfiguration?.accounts || []).length) + (selected.paymentCalendar || selected.contractConfiguration?.paymentCalendar ? 1 : 0);
  const versionTitle = `Version ${selected.version_no}${isAmendment ? ` · amendment of version ${predecessor?.version_no ?? '?'}` : selected.version_no === 1 ? ' · the original agreement' : ''}`;
  // F-MK-084: a SOURCE cell opens the source panel at the row's own document and page. An amendment
  // reads rows from two documents, so its cells say which.
  const sourceCell = (request: SourceRequest, extra?: string | null) => {
    const found = provenance.resolve(request, selected);
    return <SourceLink request={request} provenance={found} extra={extra}
      documentTag={isAmendment ? provenance.documentOf(found.jobId).short : null}
      active={openSource?.uid === request.uid} onOpen={setOpenSource} />;
  };
  const recordPath = isAmendment ? amendmentReviewPath(Number(facilityId || selected.facility_id), selected.id) : onboardingVersionPath(selected.id);

  // The one primary action an active contract has; offered in the summary strip and in the decision bar.
  const amendActions = selected.state === 'ACTIVE' && canMake && !openAmendment && !amendmentOpen ? <>
    <SmallButton type="button" onClick={() => navigate(DOCUMENT_PROCESSING_PATH)} title="Upload the executed amendment as a PDF under Middle Office → Document Processing"><FileText size={13} /> Upload the amendment (PDF)</SmallButton>
    <SmallButton type="button" onClick={() => setAmendmentOpen(true)} title={`Enter the amendment with every term and schedule prefilled from version ${selected.version_no}`} style={{ color: '#e0f2fe', borderColor: '#7dd3fc', background: 'rgba(14,116,144,.25)' }}><CheckCircle2 size={13} /> Enter the amendment</SmallButton>
  </> : null;
  const sections = [
    { id: 'contract-terms', label: 'Terms' },
    { id: 'contract-eligibility', label: 'Eligibility' },
    { id: 'contract-covenants', label: 'Covenants' },
    { id: 'contract-waterfall', label: 'Waterfall' },
    ...(facilityId && selected.state === 'ACTIVE' ? [{ id: 'contract-accrual', label: 'Accrual' }] : []),
    { id: 'contract-accounts', label: 'Accounts & calendar' },
    { id: 'contract-lender-classes', label: 'Lender classes' },
    ...(selected.conditionsPrecedent.length > 0 ? [{ id: 'contract-conditions', label: 'Conditions precedent' }] : []),
  ];
  const jumpTo = (event: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const decision = (() => {
    if (amendmentOpen) return null;
    if (openAmendment) return {
      title: `Amendment version ${openAmendment.version_no} is ${businessState(openAmendment.state).toLowerCase()}`,
      state: `Effective ${date(openAmendment.effective_from)}. One amendment at a time: withdraw it from its review to start another.`,
      actions: <Link to={amendmentReviewPath(Number(facilityId || openAmendment.facility_id), openAmendment.id)}><Button $variant="primary" type="button">Open its review</Button></Link>,
    };
    if (selected.state === 'VERIFIED') return {
      title: activationRefusal ? 'Not ready to activate' : `Activate version ${selected.version_no}`,
      state: activationRefusal || 'Activation materializes the verified contract and supersedes the predecessor from the effective date. It posts no exposure, cash or borrowing base.',
      actions: <Button $variant="primary" type="button" disabled={busy || Boolean(activationRefusal)} onClick={activate}><CheckCircle2 size={14} /> Activate version {selected.version_no}</Button>,
    };
    if (selected.state === 'ACTIVE') return {
      title: 'Amend this contract',
      state: canMake
        ? `Upload the executed amendment, or enter it with every term and schedule prefilled from version ${selected.version_no}. A checker reviews and activates it.`
        : 'A portfolio manager, trader, operations user or administrator enters an amendment; a checker activates it.',
      actions: canMake ? <>
        <Button type="button" onClick={() => navigate(DOCUMENT_PROCESSING_PATH)}><FileText size={14} /> Upload the amendment (PDF)</Button>
        <Button $variant="primary" type="button" onClick={() => setAmendmentOpen(true)}><CheckCircle2 size={14} /> Enter the amendment</Button>
      </> : null,
    };
    const successor = versions.find(item => item.predecessor_version_id === selected.id && item.state === 'ACTIVE');
    return {
      title: businessState(selected.state),
      state: selected.state === 'SUPERSEDED'
        ? `Superseded${successor ? ` by version ${successor.version_no} from ${date(successor.effective_from)}` : ''}; kept as history. Select a version above to read it as it stood.`
        : selected.state === 'WITHDRAWN' ? 'Withdrawn before activation; kept as history.'
          : selected.state === 'REJECTED' ? 'Refused by the checker; kept as history.'
            : `Effective ${date(selected.effective_from)}.`,
      actions: isAmendment && !['SUPERSEDED', 'WITHDRAWN', 'REJECTED'].includes(selected.state)
        ? <Link to={amendmentReviewPath(Number(facilityId || selected.facility_id), selected.id)}><Button type="button">Open its review</Button></Link> : null,
    };
  })();

  return <Shell>
    <Journey aria-label="How the contract changes">
      <li data-current={journeyStep === 1}><span className={`step-icon${active ? ' done' : ''}`}>{active ? <Check size={14} /> : 1}</span><div><strong>An agreement is in force</strong><small>{active ? `Version ${active.version_no}, effective ${date(active.effective_from)}` : 'No version is active yet'}</small></div></li>
      <li data-current={journeyStep === 2}><span className="step-icon">2</span><div><strong>An amendment is entered</strong><small>By PDF or by hand; every term and schedule carried from the active version</small></div></li>
      <li data-current={journeyStep === 3}><span className="step-icon">3</span><div><strong>A checker activates it</strong><small>The successor supersedes from its effective date; history is never edited</small></div></li>
    </Journey>

    <Strip aria-label="This contract">
      <div>
        <span>Active contract</span>
        <strong>{active ? `Version ${active.version_no}` : 'None'}</strong>
        <small>{active ? `Effective ${date(active.effective_from)}` : 'Nothing has been activated'}</small>
      </div>
      <div>
        <span>Authority</span>
        <strong style={{ fontSize: 15 }}>{businessLabel(selected.source_document_type)}</strong>
        <small>{selected.verified_by ? `Verified by ${selected.verified_by}` : selected.reviewed_by ? `Reviewed by ${selected.reviewed_by}` : 'Verification open'}</small>
      </div>
      <div data-tone={openAmendment ? 'warn' : undefined}>
        <span>Open amendment</span>
        <strong data-tone={openAmendment ? 'warn' : 'good'}>{openAmendment ? `Version ${openAmendment.version_no}` : 'None'}</strong>
        <small>{openAmendment ? businessState(openAmendment.state) : 'Nothing is waiting on a checker'}</small>
      </div>
      <div>
        <span>Terms</span>
        <strong>{isAmendment ? `${changedCount} changed` : verifiedCount}</strong>
        <small>{isAmendment ? `${inheritedRows.length} inherited from version ${predecessor?.version_no ?? '?'}` : `of ${selected.terms.length} verified`}</small>
      </div>
      <div>
        <span>Schedules</span>
        <strong>{scheduleCount}</strong>
        <small>{selected.eligibilityRules.length} rules · {selected.covenants.length} covenants · {selected.waterfallNodes.length} waterfall steps</small>
      </div>
      <div>
        <span>Versions</span>
        <strong>{versions.length}</strong>
        <small>Newest known {knownTime(versions[0]?.system_from)}</small>
      </div>
    </Strip>

    <Layout>
      <TaskRail aria-label="Contract versions">
        <div className="rail-heading"><h2>Versions</h2><p>Effective time and the time each was recorded, for every version.</p></div>
        <div className="tasks">
          {versions.map(version => <TaskButton key={version.id} type="button" $active={version.id === selected.id} onClick={() => selectVersion(version.id)}>
            <span className="category">{version.predecessor_version_id != null ? 'Amendment' : version.version_no === 1 ? 'Original agreement' : 'Version'}</span>
            <strong>Version {version.version_no}</strong>
            <span className="reason" style={{ color: version.state === 'ACTIVE' ? '#7cddbe' : ['VERIFIED', 'SCHEDULED'].includes(version.state) ? '#f0a24a' : INK.muted }}>
              {version.state === 'ACTIVE' ? <Check size={12} /> : <CircleDot size={12} />}{businessState(version.state)}
            </span>
            <span className="owner">Effective {date(version.effective_from)}{version.effective_to ? ` to ${date(version.effective_to)}` : ''} · {version.state === 'WITHDRAWN' && (version as { withdrawn_at?: string | null }).withdrawn_at
              ? `withdrawn ${date((version as { withdrawn_at?: string | null }).withdrawn_at)}${(version as { withdrawn_by?: string | null }).withdrawn_by ? ` by ${(version as { withdrawn_by?: string | null }).withdrawn_by}` : ''}`
              : `known since ${knownTime(version.system_from)}`}</span>
          </TaskButton>)}
        </div>
        <div className="rail-footer">
          <h3 style={{ fontSize: 14 }}><Clock3 size={13} style={{ verticalAlign: 'middle', marginRight: 6 }} />As known at</h3>
          <p style={{ marginTop: 4 }}>Which version applied on a date, as the record stood at a moment. Supersession never edits history.</p>
          <KnownAt style={{ marginTop: 10 }}>
            <label>Effective on<DateField aria-label="Effective on" value={effectiveAt} onChange={setEffectiveAt} /></label>
            <label>Known at<div className="pair"><DateField aria-label="Known at date" value={knownAtDate} onChange={setKnownAtDate} /><input type="time" aria-label="Known at time" value={knownAtTime} onChange={e => setKnownAtTime(e.target.value)} /></div></label>
            <SmallButton type="button" disabled={!effectiveAt || !knownAtDate} title={!effectiveAt || !knownAtDate ? 'Choose an effective date and a knowledge date.' : undefined} onClick={() => void resolveAsKnown()}>Show the version as known then</SmallButton>
            {asKnownError && <div className="problem">{asKnownError}</div>}
            {asKnown && <div className="verdict" role="status">
              <strong>Version {asKnown.version_no} applied on {date(effectiveAt)}</strong>
              As known {knownTime(`${knownAtDate}T${knownAtTime || '23:59'}:00`)}: {businessState(asKnown.state).toLowerCase()}, effective {date(asKnown.effective_from)}{asKnown.effective_to ? ` to ${date(asKnown.effective_to)}` : ' onward'} · {asKnown.knowledge === 'HISTORY' ? 'from the system-time history' : 'current knowledge'}
              {(() => {
                // F-S6-029: what was known, and for how long — newest first, the answer's version first,
                // zero-length knowledge rows (recorded and replaced in the same instant) left out.
                const rows = asKnown.systemHistory
                  .filter(row => row.system_from !== row.system_to)
                  .sort((a, b) => (Number(b.version_id === asKnown.id) - Number(a.version_id === asKnown.id))
                    || new Date(b.system_from).getTime() - new Date(a.system_from).getTime());
                const then = (reason: string) => plain(String(reason || '').replace(/^STATE_/, '')).toLowerCase();
                return rows.length > 0 && <ul>{rows.slice(0, 8).map(row => <li key={row.history_id}>
                  v{row.version_no} {businessState(row.state).toLowerCase()} · effective {date(row.effective_from)}{row.effective_to ? ` to ${date(row.effective_to)}` : ''} · known {knownTime(row.system_from)} → {sameLocalDay(row.system_from, row.system_to) ? knownClock(row.system_to) : knownTime(row.system_to)}, then {then(row.recorded_reason)}
                </li>)}</ul>;
              })()}
            </div>}
          </KnownAt>
        </div>
      </TaskRail>

      <Work ref={workRef} aria-label="Selected contract version">
        <div className="work-heading">
          <div>
            <h2>{amendmentOpen ? `Amending version ${selected.version_no} · this becomes version ${Math.max(0, ...versions.map(v => v.version_no)) + 1}` : versionTitle}</h2>
            <p>{amendmentOpen ? `Version ${selected.version_no} is the active contract; every term and schedule below starts from it. ` : ''}{businessState(selected.state)} · effective {date(selected.effective_from)}{selected.effective_to ? ` to ${date(selected.effective_to)}` : ''} · recorded {knownTime(((selected as { created_at?: string | null }).created_at) || selected.system_from)}{selected.activated_by ? ` · activated by ${selected.activated_by} ${dateTime(selected.activated_at)}` : selected.verified_by ? ` · verified by ${selected.verified_by}` : ''}</p>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <StatusChip tone={stateTone(selected.state)} label={businessState(selected.state)} />
            {/* F-MK-084: the version's review record — every value, who decided it and its source — read-only once settled. */}
            {!amendmentOpen && <SmallButton type="button" onClick={() => navigate(recordPath)} title={isAmendment ? 'Open this amendment\'s review record' : 'Open this version\'s contract review record'}><BookOpen size={13} /> View full record</SmallButton>}
            <SmallButton type="button" disabled={loading} onClick={() => void load(true)}><RefreshCw size={13} /> Refresh</SmallButton>
            {amendActions}
          </div>
        </div>

        {failure && <Failure role="alert">{failure}</Failure>}
        <ActionLocalFeedback feedback={localFeedback} />

        <Readable>
          {amendmentOpen && facilityId ? <>
            <StructuredAmendmentForm facilityId={facilityId} active={selected}
              onStaged={version => navigate(amendmentReviewPath(facilityId, version.id))}
              onCancel={() => setAmendmentOpen(false)}
              onFeedback={feedback => { setLocalFeedback(feedback); onFeedback(feedback); }} />
          </> : <>
            {!!selected.ruleClassificationCorrections?.length && <Note role="region" aria-label="Concentration rule correction" style={{ marginBottom: 14 }}>
              <strong>Correct concentration rule classification</strong>
              <p>{selected.state === 'ACTIVE'
                ? `The agreement's pool limits were imported as individual-loan tests. Prepare a corrected version effective ${date(selected.ruleCorrectionEffectiveFrom || null)} for independent review.`
                : 'This version corrects how the existing agreement limits are applied. The source clauses and contractual percentages are retained.'}</p>
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>{selected.ruleClassificationCorrections.map(correction => <li key={correction.ruleId}>
                <strong>{correction.ruleName}: {correction.capPct}% of eligible pool</strong> — individual-loan test → {businessLabel(correction.toType)} limit{correction.sourcePage ? ` · page ${correction.sourcePage}` : ''}
              </li>)}</ul>
              {selected.state === 'ACTIVE' && <div style={{ marginTop: 10 }}>{canMake
                ? <Button $variant="primary" type="button" disabled={busy} onClick={() => void prepareRuleCorrection()}>Prepare the correction for a checker</Button>
                : <span style={{ color: INK.muted }}>A maker prepares this correction; an independent checker activates it.</span>}</div>}
            </Note>}

            <WorkspaceGuide><h2>{isAmendment ? 'Understand the amendment' : 'Read the agreement with its evidence'}</h2><p>Version {selected.version_no} · effective {date(selected.effective_from)}. {isAmendment ? `${changedCount} commercial terms changed. Review before and after values, then the eligibility, covenant, waterfall and calendar sections for operational impact.` : 'Search commercial terms or use the section links. Select a source beside any term to inspect its clause and review history.'}</p></WorkspaceGuide>
            <SectionNav aria-label="Sections of this version">
              <span>Jump to</span>
              {sections.map(section => <a key={section.id} href={`#${section.id}`} onClick={event => jumpTo(event, section.id)}>{section.label}</a>)}
            </SectionNav>

            <Card id="contract-terms" style={{ scrollMarginTop: 160 }}>
              <CardHead><div><h2>Terms</h2><p>{isAmendment
                ? changedCount ? `${changedCount} commercial term${changedCount === 1 ? '' : 's'} changed by this amendment; the rest are inherited from version ${predecessor?.version_no ?? '?'} with their clauses.` : `No commercial term changes; every term is inherited from version ${predecessor?.version_no ?? '?'} with its clause.`
                : 'The commercial terms of the agreement, each with the clause it was read from.'}</p></div></CardHead>
              <WorkspaceTools><input type="search" aria-label="Search commercial terms" placeholder="Find a term, value or source clause…" value={termSearch} onChange={e => setTermSearch(e.target.value)} />{isAmendment && <label><input type="checkbox" checked={changedOnly} onChange={e => setChangedOnly(e.target.checked)} /> Changed terms only</label>}<span>{visibleTerms.length} of {termRows.length} terms</span></WorkspaceTools>
              {visibleTerms.length === 0 && termRows.length > 0 && <Empty>No commercial terms match. Clear the search or use the section links for schedules and rules.</Empty>}
              {isAmendment && changedGroups.map(group => <React.Fragment key={group.name}>
                <GroupHead>{group.name}</GroupHead>
                <Table>
                  <thead><tr><th>Term</th><th>Before → after</th><th>Source</th></tr></thead>
                  <tbody>{group.rows.map(({ term, before, after, material }) => <tr key={term.id}>
                    <td><strong>{businessLabel(term.term_key)}</strong>{material && <> <Chip $colour="#f0a24a">Material</Chip></>}</td>
                    <td><Before>{readJsonValue(before) == null ? 'not stated' : displayContractValue(before)}</Before><Arrow>→</Arrow><strong>{displayContractValue(after, term.value_presence)}</strong></td>
                    <td className="source">{sourceCell(termRequest(term, after, `Was ${readJsonValue(before) == null ? 'not stated' : displayContractValue(before)}; changed by this amendment.`, material), term.verified_by ? `by ${term.verified_by}` : null)}</td>
                  </tr>)}</tbody>
                </Table>
              </React.Fragment>)}
              {isAmendment && inheritedRows.length > 0 && <details open={Boolean(termSearch) || undefined} style={{ marginTop: 10 }}>
                <summary style={{ cursor: 'pointer', color: INK.accentSoft, fontSize: 13 }}>{inheritedRows.length} inherited term{inheritedRows.length === 1 ? '' : 's'}, unchanged</summary>
                <Table>
                  <thead><tr><th>Term</th><th>Value</th><th>Source</th></tr></thead>
                  <tbody>{inheritedRows.map(({ term, after }) => <tr key={term.id} data-carried="true">
                    <td>{businessLabel(term.term_key)}</td>
                    <td>{displayContractValue(after, term.value_presence)}</td>
                    <td className="source">{sourceCell(termRequest(term, after, null, false))}</td>
                  </tr>)}</tbody>
                </Table>
              </details>}
              {!isAmendment && groupedTerms.map(group => <React.Fragment key={group.name}>
                <GroupHead>{group.name}</GroupHead>
                <Table>
                  <thead><tr><th>Term</th><th>Value</th><th>Source</th></tr></thead>
                  <tbody>{group.rows.map(({ term, after }) => <tr key={term.id}>
                    <td>{businessLabel(term.term_key)}{term.required_for_activation && term.value_presence === 'UNKNOWN' && <> <Chip $colour="#f0a24a">Needs review</Chip></>}</td>
                    <td>{displayContractValue(after, term.value_presence)}</td>
                    <td className="source">{sourceCell(termRequest(term, after, null, term.economically_material), confidence(term.extraction_confidence))}</td>
                  </tr>)}</tbody>
                </Table>
              </React.Fragment>)}
              {termRows.length === 0 && <Empty>No commercial terms are recorded on this version.</Empty>}
            </Card>

            <Card id="contract-eligibility" style={{ marginTop: 14, scrollMarginTop: 160 }}>
              <CardHead><div><h2>Eligibility and concentration</h2><p>The collateral tests this version authorises. Each rule names the field it tests and the clause it comes from; the Rule Workbench tightens them by amendment.</p></div></CardHead>
              {selected.eligibilityRules.length ? <Table>
                <thead><tr><th>Rule</th><th>Test</th><th className="num">Advance rate</th><th>State</th><th>Source</th></tr></thead>
                <tbody>{selected.eligibilityRules.map(rule => <tr key={rule.id}>
                  <td><strong>{rule.rule_name}</strong><div style={{ color: INK.muted, fontSize: 12 }}>{plain(rule.rule_type)}</div></td>
                  <td>{rule.field_name ? `${businessLabel(rule.field_name)} ${rule.operator || ''} ${rule.threshold_value != null ? number(rule.threshold_value, 4) : rule.threshold_text || ''}`.trim() : rule.threshold_text || '—'}</td>
                  <td className="num">{rule.advance_rate_pct != null ? `${number(rule.advance_rate_pct)}%` : '—'}</td>
                  <td>{plain(rule.authority_status)}</td>
                  <td className="source">{sourceCell({
                    uid: `rule:${rule.id}`, table: 'eligibilityRules', row: rule as unknown as Record<string, unknown>, kind: 'RULE', section: 'eligibility',
                    label: rule.rule_name, group: plain(rule.rule_type),
                    value: rule.field_name ? `${businessLabel(rule.field_name)} ${rule.operator || ''} ${rule.threshold_value != null ? number(rule.threshold_value, 4) : rule.threshold_text || ''}`.trim() : rule.threshold_text || '—',
                    detail: rule.advance_rate_pct != null ? `Advance rate ${number(rule.advance_rate_pct)}% · ${plain(rule.authority_status).toLowerCase()}` : plain(rule.authority_status),
                    page: rule.source_page, sectionRef: rule.source_section, clause: rule.source_clause,
                  })}</td>
                </tr>)}</tbody>
              </Table> : <Empty>No eligibility or concentration rules are recorded on this version.</Empty>}
            </Card>

            <Card id="contract-covenants" style={{ marginTop: 14, scrollMarginTop: 160 }}>
              <CardHead><div><h2>Covenants</h2><p>The financial and portfolio covenants monitored against this version, with their thresholds and formulas.</p></div></CardHead>
              {selected.covenants.length ? <Table>
                <thead><tr><th>Covenant</th><th>Threshold</th><th>Formula</th><th>State</th><th>Source</th></tr></thead>
                <tbody>{selected.covenants.map(covenant => <tr key={covenant.id}>
                  <td><strong>{covenant.covenant_name}</strong></td>
                  <td>{plain(covenant.threshold_type)} {covenant.threshold_value != null ? number(covenant.threshold_value, 4) : ''}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{covenant.formula_expression || '—'}</td>
                  <td>{plain(covenant.state)}</td>
                  <td className="source">{sourceCell({
                    uid: `covenant:${covenant.id}`, table: 'covenants', row: covenant as unknown as Record<string, unknown>, kind: 'COVENANT', section: 'covenants',
                    label: covenant.covenant_name, value: `${plain(covenant.threshold_type)} ${covenant.threshold_value != null ? number(covenant.threshold_value, 4) : ''}`.trim(),
                    detail: covenant.formula_expression ? `Formula ${covenant.formula_expression}` : null,
                    page: covenant.source_page, sectionRef: covenant.source_section, clause: covenant.source_clause,
                  })}</td>
                </tr>)}</tbody>
              </Table> : <Empty>No covenants are recorded on this version.</Empty>}
            </Card>

            <Card id="contract-waterfall" style={{ marginTop: 14, scrollMarginTop: 160 }}>
              <CardHead><div><h2>Waterfall and consequences</h2><p>The priority of payments the Collections tab runs, and the consequences a failed test triggers.</p></div></CardHead>
              {selected.waterfallNodes.length ? <Table>
                <thead><tr><th className="num">Priority</th><th>Step</th><th>Payment</th><th>Mode</th><th>To</th><th>Source</th></tr></thead>
                <tbody>{[...selected.waterfallNodes].sort((a, b) => a.priority_order - b.priority_order).map(node => <tr key={node.id} data-carried={node.enabled ? undefined : 'true'}>
                  <td className="num">{node.priority_order}</td>
                  <td><strong>{node.node_name}</strong>{!node.enabled && <> <Chip $colour={INK.muted}>Disabled</Chip></>}</td>
                  <td>{plain(node.payment_type)}</td>
                  <td>{plain(node.mode)}</td>
                  <td className="muted">{node.lender_class_key ? `Class ${node.lender_class_key}` : node.destination_account_key ? `${plain(node.destination_account_key)} account` : '—'}</td>
                  <td className="source">{sourceCell({
                    uid: `node:${node.id}`, table: 'waterfallNodes', row: node as unknown as Record<string, unknown>, kind: 'SCHEDULE', section: 'schedules',
                    label: node.node_name, group: 'Waterfall', value: `Priority ${node.priority_order} · ${plain(node.payment_type)}`,
                    detail: `${plain(node.mode)}${node.lender_class_key ? ` · to class ${node.lender_class_key}` : node.destination_account_key ? ` · to the ${plain(node.destination_account_key).toLowerCase()} account` : ''}${node.enabled ? '' : ' · disabled'}`,
                    clause: node.source_clause,
                  })}</td>
                </tr>)}</tbody>
              </Table> : <Empty>No contractual waterfall is present on this version. The Collections tab cannot run a payment date until an amendment provides one.</Empty>}
              {selected.consequences.length > 0 && <>
                <GroupHead>Consequences</GroupHead>
                <Table>
                  <thead><tr><th>Consequence</th><th>Applies to</th><th>Source</th></tr></thead>
                  <tbody>{selected.consequences.map(item => <tr key={item.id}>
                    {/* Raw enum finding: the consequence reads in words; the stored key stays in the tooltip. */}
                    <td><strong>{labelFor(item.consequence_type)}</strong><div style={{ color: INK.muted, fontSize: 12 }} title={item.consequence_key}>{labelFor(item.consequence_key)}</div></td>
                    <td>{plain(item.target_type)}{item.target_key ? ` · ${labelFor(item.target_key)}` : ''}</td>
                    <td className="source">{sourceCell({
                      uid: `consequence:${item.id}`, table: 'consequences', row: item as unknown as Record<string, unknown>, kind: 'SCHEDULE', section: 'schedules',
                      label: labelFor(item.consequence_type), group: 'Consequence', value: `${plain(item.target_type)}${item.target_key ? ` · ${labelFor(item.target_key)}` : ''}`,
                      detail: labelFor(item.consequence_key), clause: item.source_clause,
                    })}</td>
                  </tr>)}</tbody>
                </Table>
              </>}
            </Card>

            <div style={{ marginTop: 14 }}>
              {/* WS7 D-9: the accrual basis is a contract term; shown with the schedules, set by a checker with its clause. */}
              {facilityId && selected.state === 'ACTIVE' && <div id="contract-accrual" style={{ scrollMarginTop: 160 }}><AccrualBasisPanel facilityId={facilityId} canCheck={canCheck} onFeedback={onFeedback} /></div>}
              {/* Work plan item F: the facility structure (phase, revolving period, amortisation) and the governed term-out. */}
              {facilityId && selected.state === 'ACTIVE' && <div id="contract-structure" style={{ scrollMarginTop: 160 }}><TermOutPanel facilityId={facilityId} canMake={canMake} canCheck={canCheck} currentUser={getCurrentOperatorUsername()} onFeedback={onFeedback} /></div>}
              <div id="contract-accounts" style={{ scrollMarginTop: 160 }} />
              <CollectionsSchedulesPanel
                version={selected}
                canComplete={canMake}
                currentUser={getCurrentOperatorUsername() || ''}
                onChanged={() => load(false)}
                onFeedback={feedback => { setLocalFeedback(feedback); onFeedback(feedback); }}
                onAddSchedules={selected.state === 'ACTIVE' && canMake && !openAmendment ? () => setAmendmentOpen(true) : undefined}
                renderSource={request => sourceCell(request)}
              />
            </div>

            <Card id="contract-lender-classes" style={{ marginTop: 14, scrollMarginTop: 160 }}>
              <CardHead><div><h2>Lender classes</h2><p>The classes the waterfall pays and their commitments.</p></div></CardHead>
              {selected.lenderClasses.length ? <Table>
                <thead><tr><th>Class</th><th>Seniority</th><th className="num">Priority</th><th className="num">Commitment</th><th>Source</th></tr></thead>
                <tbody>{[...selected.lenderClasses].sort((a, b) => a.priority_order - b.priority_order).map(item => <tr key={item.id}>
                  <td><strong>{item.class_name}</strong><div style={{ color: INK.muted, fontSize: 12 }}>{item.class_key}</div></td>
                  <td>{plain(item.seniority)}</td>
                  <td className="num">{item.priority_order}</td>
                  <td className="num">{money(item.commitment_amount, item.currency || currency)}</td>
                  <td className="source">{sourceCell({
                    uid: `class:${item.id}`, table: 'lenderClasses', row: item as unknown as Record<string, unknown>, kind: 'SCHEDULE', section: 'schedules',
                    label: item.class_name, group: 'Lender class', value: money(item.commitment_amount, item.currency || currency),
                    detail: `${plain(item.seniority)} · priority ${item.priority_order}`, clause: item.source_clause,
                  })}</td>
                </tr>)}</tbody>
              </Table> : <Empty>No lender classes are recorded on this version; the waterfall pays the facility as a single class.</Empty>}
            </Card>

            {selected.conditionsPrecedent.length > 0 && <Card id="contract-conditions" style={{ marginTop: 14, scrollMarginTop: 160 }}>
              <CardHead><div><h2>Conditions precedent</h2><p>{openActivationConditions.length ? `${openActivationConditions.length} still open for activation.` : 'Every activation condition is satisfied or waived.'}</p></div></CardHead>
              <Table>
                <thead><tr><th>Condition</th><th>Required for</th><th>Due</th><th>Status</th><th>Source</th></tr></thead>
                <tbody>{selected.conditionsPrecedent.map(item => <tr key={item.id}>
                  <td>{item.description}</td>
                  <td>{plain(item.required_for)}</td>
                  <td>{item.due_date ? date(item.due_date) : '—'}</td>
                  <td>{['SATISFIED', 'WAIVED'].includes(item.status) ? <Chip $colour="#7cddbe">{plain(item.status)}</Chip> : <Chip $colour="#f0a24a">{plain(item.status)}</Chip>}</td>
                  <td className="source">{sourceCell({
                    uid: `cp:${item.id}`, table: 'conditionsPrecedent', row: item as unknown as Record<string, unknown>, kind: 'CONDITION', section: 'conditions',
                    label: item.description, value: plain(item.status), detail: `Required for ${plain(item.required_for).toLowerCase()}${item.due_date ? ` · due ${date(item.due_date)}` : ''}`,
                    clause: item.source_clause,
                  })}</td>
                </tr>)}</tbody>
              </Table>
            </Card>}
          </>}
        </Readable>

        {decision && <Bar aria-label="Next action">
          <div>
            <strong>{activationRefusal && selected.state === 'VERIFIED' && <AlertTriangle size={13} style={{ verticalAlign: 'middle', marginRight: 6, color: '#fcd34d' }} />}{decision.title}</strong>
            <span className="state">{decision.state}</span>
          </div>
          {decision.actions && <div className="actions">{decision.actions}</div>}
        </Bar>}
      </Work>
    </Layout>
    {openSource && !amendmentOpen && (() => {
      const found = provenance.resolve(openSource, selected);
      const carried = found.version && found.version.id !== selected.id ? found.version : null;
      return <ContractSourceDrawer request={openSource} provenance={found} selected={selected} document={provenance.documentOf(found.jobId)}
        chip={carried ? { tone: 'neutral', label: `Inherited from version ${carried.version_no}` } : { tone: stateTone(selected.state), label: businessState(selected.state) }}
        onClose={closeSource} />;
    })()}
  </Shell>;
};

export default ContractWorkspace;
